import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { vacancyApi, applicantApi, jobDescriptionApi, interviewApi, referenceCheckApi, screeningApi } from '../../api/recruitment';
import { departmentsApi } from '../../api/admin/departments';
import { positionsApi } from '../../api/admin/positions';
import { locationsApi } from '../../api/admin/locations';
import { ApiError } from '../../api/client';
import { DataTable } from '../../components/DataTable';
import { IconActionButton } from '../../components/IconActionButton';
import { RotateCcw, Trash2, UserPlus } from 'lucide-react';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', published: 'Published', applications: 'Accepting Applications', screening: 'Screening',
  longlisting: 'Longlisting', shortlisting: 'Shortlisting', assessment: 'Assessment', interview: 'Interview',
  reference_check: 'Reference Check', selection: 'Selection', approval: 'Awaiting Approval', offer: 'Offer',
  hired: 'Hired', closed: 'Closed', cancelled: 'Cancelled',
};

const APPLICANT_STATUSES = ['applied', 'screened', 'longlisted', 'shortlisted', 'assessed', 'interviewed', 'reference_checked', 'selected', 'offered', 'hired', 'rejected', 'withdrawn'];

export function VacancyDetailPage() {
  const { vacancyId } = useParams<{ vacancyId: string }>();
  const id = Number(vacancyId);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const vacancyQuery = useQuery({ queryKey: ['vacancy', id], queryFn: () => vacancyApi.get(id) });
  const applicantsQuery = useQuery({ queryKey: ['applicants', id], queryFn: () => applicantApi.list(id) });
  const jobDescriptionQuery = useQuery({ queryKey: ['job-description', id], queryFn: () => jobDescriptionApi.get(id) });
  const panelQuery = useQuery({ queryKey: ['panel', id], queryFn: () => interviewApi.getPanel(id) });
  const departmentsQuery = useQuery({ queryKey: ['admin-departments'], queryFn: () => departmentsApi.list() });
  const positionsQuery = useQuery({ queryKey: ['admin-positions'], queryFn: () => positionsApi.list() });
  const locationsQuery = useQuery({ queryKey: ['admin-locations'], queryFn: () => locationsApi.list() });
  const coreFieldsForm = useForm<{ positionId: string; departmentId: string; dutyStationId: string; employmentType: string; grade: string; autoShortlistThreshold: string; openingDate: string; closingDate: string }>({
    values: vacancyQuery.data ? {
      positionId: String(vacancyQuery.data.data.position_id ?? ''), departmentId: String(vacancyQuery.data.data.department_id ?? ''),
      dutyStationId: String(vacancyQuery.data.data.duty_station_id ?? ''), employmentType: vacancyQuery.data.data.employment_type ?? '',
      grade: vacancyQuery.data.data.grade ?? '', autoShortlistThreshold: vacancyQuery.data.data.auto_shortlist_threshold != null ? String(vacancyQuery.data.data.auto_shortlist_threshold) : '',
      openingDate: vacancyQuery.data.data.opening_date ? String(vacancyQuery.data.data.opening_date).slice(0, 10) : '',
      closingDate: vacancyQuery.data.data.closing_date ? String(vacancyQuery.data.data.closing_date).slice(0, 10) : '',
    } : undefined,
  });
  const coreFieldsMutation = useMutation({
    mutationFn: (v: { positionId: string; departmentId: string; dutyStationId: string; employmentType: string; grade: string; autoShortlistThreshold: string; openingDate: string; closingDate: string }) =>
      vacancyApi.update(id, {
        positionId: v.positionId ? Number(v.positionId) : null, departmentId: v.departmentId ? Number(v.departmentId) : null,
        dutyStationId: v.dutyStationId ? Number(v.dutyStationId) : null, employmentType: v.employmentType || null, grade: v.grade || null,
        autoShortlistThreshold: v.autoShortlistThreshold ? Number(v.autoShortlistThreshold) : null,
        openingDate: v.openingDate || null, closingDate: v.closingDate || null,
      }),
    onSuccess: () => { showSuccess('Vacancy details saved.'); queryClient.invalidateQueries({ queryKey: ['vacancy', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });
  const { register, handleSubmit, reset } = useForm<{ fullName: string; email: string; phone?: string }>();
  const jdForm = useForm<Record<string, string>>({ values: (jobDescriptionQuery.data?.data ?? {}) as Record<string, string> });
  const panelForm = useForm<{ userId: string; panelRole?: string }>();
  const [showJobDescription, setShowJobDescription] = useState(false);
  const [activeApplicantId, setActiveApplicantId] = useState<number | null>(null);

  const jdMutation = useMutation({
    mutationFn: (values: Record<string, string>) => jobDescriptionApi.upsert(id, values),
    onSuccess: () => { showSuccess('Job description saved.'); queryClient.invalidateQueries({ queryKey: ['job-description', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save job description.'),
  });

  const addPanelistMutation = useMutation({
    mutationFn: (values: { userId: string; panelRole?: string }) => interviewApi.addPanelist(id, { userId: Number(values.userId), panelRole: values.panelRole }),
    onSuccess: () => { showSuccess('Panelist added.'); queryClient.invalidateQueries({ queryKey: ['panel', id] }); panelForm.reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add panelist.'),
  });

  const removePanelistMutation = useMutation({
    mutationFn: (panelistId: number) => interviewApi.removePanelist(panelistId),
    onSuccess: () => { showSuccess('Panelist removed.'); queryClient.invalidateQueries({ queryKey: ['panel', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to remove panelist.'),
  });

  const advanceMutation = useMutation({
    mutationFn: () => vacancyApi.advance(id),
    onSuccess: (result) => { showSuccess(`Vacancy moved to: ${STATUS_LABELS[result.data.status] ?? result.data.status}`); queryClient.invalidateQueries({ queryKey: ['vacancy', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to advance vacancy.'),
  });

  const regressMutation = useMutation({
    mutationFn: () => vacancyApi.regress(id),
    onSuccess: (result) => { showSuccess(`Vacancy returned to: ${STATUS_LABELS[result.data.status] ?? result.data.status}`); queryClient.invalidateQueries({ queryKey: ['vacancy', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to return to the previous stage.'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => vacancyApi.cancel(id),
    onSuccess: () => { showSuccess('Vacancy cancelled.'); queryClient.invalidateQueries({ queryKey: ['vacancy', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to cancel vacancy.'),
  });

  const decideMutation = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') => vacancyApi.decide(id, { decision }),
    onSuccess: (result) => { showSuccess(`Decision recorded - status: ${result.data.status}`); queryClient.invalidateQueries({ queryKey: ['vacancy', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record decision.'),
  });

  const addApplicantMutation = useMutation({
    mutationFn: (values: { fullName: string; email: string; phone?: string }) => applicantApi.create(id, values),
    onSuccess: () => { showSuccess('Applicant added.'); queryClient.invalidateQueries({ queryKey: ['applicants', id] }); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add applicant.'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ applicantId, status }: { applicantId: number; status: string }) => applicantApi.updateStatus(applicantId, { status }),
    onSuccess: () => { showSuccess('Applicant status updated.'); queryClient.invalidateQueries({ queryKey: ['applicants', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to update applicant.'),
  });

  const convertMutation = useMutation({
    mutationFn: (applicantId: number) => applicantApi.convert(applicantId),
    onSuccess: (result) => { showSuccess(`Converted to staff - ${result.data.staffId}`); queryClient.invalidateQueries({ queryKey: ['applicants', id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to convert applicant.'),
  });

  if (!vacancyQuery.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const vacancy = vacancyQuery.data.data;

  return (
    <div className="page">
      <Link to="/hr/vacancies">&larr; Back to Vacancies</Link>
      <div className="page-header"><h1>{vacancy.vacancy_reference}</h1></div>
      <p className="muted">Status: <strong>{STATUS_LABELS[vacancy.status] ?? vacancy.status}</strong></p>

      <section className="card">
        <h2>Vacancy Details</h2>
        <p>{vacancy.job_description ?? 'No description yet.'}</p>
        <div className="form-actions">
          {vacancy.status !== 'closed' && vacancy.status !== 'cancelled' && (
            <button className="btn-primary" onClick={() => advanceMutation.mutate()} disabled={advanceMutation.isPending}>
              {advanceMutation.isPending ? 'Advancing…' : 'Advance to Next Stage'}
            </button>
          )}
          {vacancy.status !== 'draft' && vacancy.status !== 'approval' && vacancy.status !== 'hired' && vacancy.status !== 'closed' && vacancy.status !== 'cancelled' && (
            <IconActionButton icon={RotateCcw} label="Return to Previous Stage" disabled={regressMutation.isPending} onClick={() => regressMutation.mutate()} />
          )}
          {vacancy.status !== 'closed' && vacancy.status !== 'cancelled' && vacancy.status !== 'hired' && (
            <button className="button-danger" onClick={() => cancelMutation.mutate()} disabled={cancelMutation.isPending}>
              {cancelMutation.isPending ? 'Cancelling…' : 'Cancel Vacancy'}
            </button>
          )}
        </div>
        {vacancy.status === 'cancelled' && <p className="muted">This vacancy has been cancelled.</p>}
        {vacancy.status === 'approval' && (
          <div className="approval-panel">
            <h3>Hiring decision requested</h3>
            <div className="form-actions">
              <button className="btn-primary" onClick={() => decideMutation.mutate('approved')} disabled={decideMutation.isPending}>Approve</button>
              <button className="button-danger" onClick={() => decideMutation.mutate('rejected')} disabled={decideMutation.isPending}>Reject</button>
            </div>
          </div>
        )}
      </section>

      <section className="card">
        <h2>Core Details</h2>
        <p className="muted">Position, department, duty station and employment type determine how this vacancy appears on the public careers page - these were previously not editable anywhere.</p>
        <form onSubmit={coreFieldsForm.handleSubmit((v) => coreFieldsMutation.mutate(v))}>
          <label>
            Position
            <select {...coreFieldsForm.register('positionId')}>
              <option value="">- select -</option>
              {positionsQuery.data?.data.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </label>
          <label>
            Department
            <select {...coreFieldsForm.register('departmentId')}>
              <option value="">- select -</option>
              {departmentsQuery.data?.data.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>
          <label>
            Duty Station
            <select {...coreFieldsForm.register('dutyStationId')}>
              <option value="">- select -</option>
              {locationsQuery.data?.data.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </label>
          <label>
            Employment Type
            <select {...coreFieldsForm.register('employmentType')}>
              <option value="">- select -</option>
              <option value="full_time">Full-Time</option><option value="part_time">Part-Time</option>
              <option value="consultant">Consultant</option><option value="intern">Intern</option><option value="volunteer">Volunteer</option>
            </select>
          </label>
          <label>Grade<input {...coreFieldsForm.register('grade')} /></label>
          <label>Opening Date<input type="date" {...coreFieldsForm.register('openingDate')} /></label>
          <label>Closing Date (application deadline)<input type="date" {...coreFieldsForm.register('closingDate')} /></label>
          <label>
            Auto-Shortlist Threshold (0-10, optional)
            <input type="number" step="0.1" min="0" max="10" placeholder="Leave blank to keep shortlisting fully manual" {...coreFieldsForm.register('autoShortlistThreshold')} />
          </label>
          <div className="form-actions"><button type="submit" className="btn-primary" disabled={coreFieldsMutation.isPending}>{coreFieldsMutation.isPending ? 'Saving…' : 'Save Core Details'}</button></div>
        </form>
      </section>

      <section className="card">
        <div className="page-header"><h2>Job Description &amp; Person Specification</h2><button onClick={() => setShowJobDescription((v) => !v)}>{showJobDescription ? 'Hide' : 'Edit'}</button></div>
        {showJobDescription && (
          <form onSubmit={jdForm.handleSubmit((values) => jdMutation.mutate(values))}>
            <label>Job Purpose<textarea rows={2} {...jdForm.register('jobPurpose')} /></label>
            <label>Key Responsibilities<textarea rows={3} {...jdForm.register('keyResponsibilities')} /></label>
            <label>Minimum Education<input {...jdForm.register('minimumEducation')} /></label>
            <label>Required Experience<textarea rows={2} {...jdForm.register('requiredExperience')} /></label>
            <label>Years of Experience<input {...jdForm.register('yearsExperience')} /></label>
            <label>Language Requirements<input {...jdForm.register('languageRequirements')} /></label>
            <label>
              Work Arrangement
              <select {...jdForm.register('workArrangement')}>
                <option value="">- not set -</option>
                <option value="office">Office</option><option value="field">Field</option><option value="remote">Remote</option><option value="hybrid">Hybrid</option>
              </select>
            </label>
            <label>Safeguarding / PSEA Responsibilities<textarea rows={2} {...jdForm.register('safeguardingResponsibilities')} /></label>
            <div className="form-actions"><button type="submit" className="btn-primary" disabled={jdMutation.isPending}>Save</button></div>
          </form>
        )}
      </section>

      <section className="card">
        <h2>Interview Panel</h2>
        <form className="inline-form" onSubmit={panelForm.handleSubmit((values) => addPanelistMutation.mutate(values))}>
          <label>User ID<input {...panelForm.register('userId', { required: true })} /></label>
          <label>Panel Role<input placeholder="e.g. Chair, Technical, HR" {...panelForm.register('panelRole')} /></label>
          <button type="submit" disabled={addPanelistMutation.isPending}>Add Panelist</button>
        </form>
        {panelQuery.data && (
          <ul className="deadline-list">
            {panelQuery.data.data.map((p) => (
              <li key={p.id}>{p.panelist_name} {p.panel_role && <span className="muted">({p.panel_role})</span>} <IconActionButton icon={Trash2} label="Remove" danger onClick={() => removePanelistMutation.mutate(p.id)} /></li>
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <h2>Applicants</h2>
        <form className="inline-form" onSubmit={handleSubmit((values) => addApplicantMutation.mutate(values))}>
          <label>Full Name<input {...register('fullName', { required: true })} /></label>
          <label>Email<input type="email" {...register('email', { required: true })} /></label>
          <label>Phone<input {...register('phone')} /></label>
          <button type="submit" disabled={addApplicantMutation.isPending}>Add Applicant</button>
        </form>

        {applicantsQuery.data && (
          <DataTable
            rows={applicantsQuery.data.data}
            rowKey={(a) => a.id}
            emptyMessage="No applicants yet."
            columns={[
              { header: 'Name', render: (a) => a.full_name },
              { header: 'Email', render: (a) => a.email },
              {
                header: 'Status',
                render: (a) => (
                  <select value={a.status} onChange={(e) => statusMutation.mutate({ applicantId: a.id, status: e.target.value })} disabled={!!a.converted_employee_id}>
                    {APPLICANT_STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                  </select>
                ),
              },
              {
                header: '',
                render: (a) =>
                  a.converted_employee_id ? (
                    <span className="muted">Converted to staff</span>
                  ) : (
                    <span className="row-actions">
                      {(a.status === 'selected' || a.status === 'offered') && <IconActionButton icon={UserPlus} label="Convert to Staff" disabled={convertMutation.isPending} onClick={() => convertMutation.mutate(a.id)} />}
                      <button className="link-button" onClick={() => setActiveApplicantId(activeApplicantId === a.id ? null : a.id)}>{activeApplicantId === a.id ? 'Close' : 'Score / Reference'}</button>
                    </span>
                  ),
              },
            ]}
          />
        )}

        {activeApplicantId && <ApplicantScorePanel applicantId={activeApplicantId} />}
      </section>
    </div>
  );
}

/** Compact panel for interview scoring and reference checks - kept separate from the main table since not every applicant needs this open at once. */
function ApplicantScorePanel({ applicantId }: { applicantId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const scoresQuery = useQuery({ queryKey: ['scores', applicantId], queryFn: () => interviewApi.getScores(applicantId) });
  const checksQuery = useQuery({ queryKey: ['reference-checks', applicantId], queryFn: () => referenceCheckApi.list(applicantId) });
  const screeningQuery = useQuery({ queryKey: ['screening-score', applicantId], queryFn: () => screeningApi.get(applicantId) });
  const screeningForm = useForm<{ qualifications: string; experience: string; skills: string; language: string; notes?: string }>();
  const screeningMutation = useMutation({
    mutationFn: (v: { qualifications: string; experience: string; skills: string; language: string; notes?: string }) =>
      screeningApi.submit(applicantId, { qualifications: Number(v.qualifications), experience: Number(v.experience), skills: Number(v.skills), language: Number(v.language), notes: v.notes }),
    onSuccess: (result) => {
      showSuccess(result.data.autoShortlisted ? `Score submitted (${result.data.overall.toFixed(1)}) - automatically shortlisted, this vacancy's threshold was met.` : `Score submitted (${result.data.overall.toFixed(1)}).`);
      queryClient.invalidateQueries({ queryKey: ['screening-score', applicantId] });
      queryClient.invalidateQueries({ queryKey: ['applicants'] });
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to submit screening score.'),
  });
  const scoreForm = useForm<{ score?: string; recommendation?: string; comments?: string }>();
  const checkForm = useForm<{ refereeName: string; refereeRelationship?: string; outcome?: string; notes?: string }>();

  const scoreMutation = useMutation({
    mutationFn: (values: { score?: string; recommendation?: string; comments?: string }) =>
      interviewApi.submitScore(applicantId, { score: values.score ? Number(values.score) : undefined, recommendation: values.recommendation, comments: values.comments }),
    onSuccess: () => { showSuccess('Score submitted.'); queryClient.invalidateQueries({ queryKey: ['scores', applicantId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to submit score.'),
  });

  const checkMutation = useMutation({
    mutationFn: (values: { refereeName: string; refereeRelationship?: string; outcome?: string; notes?: string }) => referenceCheckApi.create(applicantId, values),
    onSuccess: () => { showSuccess('Reference check recorded.'); queryClient.invalidateQueries({ queryKey: ['reference-checks', applicantId] }); checkForm.reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record reference check.'),
  });

  return (
    <div className="card" style={{ marginTop: 'var(--spacing-md)' }}>
      <h3>Screening Scorecard</h3>
      <p className="muted">A real, structured screening decision - used before interview. If this vacancy has an auto-shortlist threshold configured, meeting it moves the applicant to Shortlisted automatically.</p>
      {screeningQuery.data?.data && (
        <p className="muted">
          Current: Qualifications {screeningQuery.data.data.qualifications_score} · Experience {screeningQuery.data.data.experience_score} ·
          Skills {screeningQuery.data.data.skills_score} · Language {screeningQuery.data.data.language_score} · Overall {screeningQuery.data.data.overall_score}
          {screeningQuery.data.data.auto_shortlisted ? ' (auto-shortlisted)' : ''}
        </p>
      )}
      <form className="inline-form" onSubmit={screeningForm.handleSubmit((v) => screeningMutation.mutate(v))}>
        <label>Qualifications (0-10)<input type="number" step="0.5" min="0" max="10" {...screeningForm.register('qualifications', { required: true })} /></label>
        <label>Experience (0-10)<input type="number" step="0.5" min="0" max="10" {...screeningForm.register('experience', { required: true })} /></label>
        <label>Skills (0-10)<input type="number" step="0.5" min="0" max="10" {...screeningForm.register('skills', { required: true })} /></label>
        <label>Language (0-10)<input type="number" step="0.5" min="0" max="10" {...screeningForm.register('language', { required: true })} /></label>
        <label>Notes<input {...screeningForm.register('notes')} /></label>
        <button type="submit" disabled={screeningMutation.isPending}>{screeningMutation.isPending ? 'Submitting…' : 'Submit Screening Score'}</button>
      </form>

      <h3>Interview Scores</h3>
      <form className="inline-form" onSubmit={scoreForm.handleSubmit((v) => scoreMutation.mutate(v))}>
        <label>Score (0-100)<input type="number" {...scoreForm.register('score')} /></label>
        <label>
          Recommendation
          <select {...scoreForm.register('recommendation')}>
            <option value="">- none -</option>
            <option value="recommend">Recommend</option><option value="not_recommend">Not Recommend</option><option value="reserve">Reserve</option>
          </select>
        </label>
        <label>Comments<input {...scoreForm.register('comments')} /></label>
        <button type="submit" disabled={scoreMutation.isPending}>Submit My Score</button>
      </form>
      {scoresQuery.data && (
        <ul className="deadline-list">
          {scoresQuery.data.data.map((s) => <li key={s.id}>{s.panelist_name}: {s.score ?? '-'} ({s.recommendation ?? 'no recommendation'})</li>)}
        </ul>
      )}

      <h3>Reference Checks</h3>
      <form className="inline-form" onSubmit={checkForm.handleSubmit((v) => checkMutation.mutate(v))}>
        <label>Referee Name<input {...checkForm.register('refereeName', { required: true })} /></label>
        <label>Relationship<input {...checkForm.register('refereeRelationship')} /></label>
        <label>
          Outcome
          <select {...checkForm.register('outcome')}>
            <option value="pending">Pending</option><option value="positive">Positive</option><option value="negative">Negative</option><option value="unable_to_reach">Unable to Reach</option>
          </select>
        </label>
        <label>Notes<input {...checkForm.register('notes')} /></label>
        <button type="submit" disabled={checkMutation.isPending}>Add</button>
      </form>
      {checksQuery.data && (
        <ul className="deadline-list">
          {checksQuery.data.data.map((c) => <li key={c.id}>{c.referee_name} - {c.outcome}{c.notes && <span className="muted"> - {c.notes}</span>}</li>)}
        </ul>
      )}
    </div>
  );
}
